import React, { Component } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListSubheader from '@material-ui/core/ListSubheader';
import IconButton from '@material-ui/core/IconButton';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';
import { DatePicker } from 'material-ui-pickers';
import MomentUtils from 'material-ui-pickers/utils/moment-utils';
import MuiPickersUtilsProvider from 'material-ui-pickers/utils/MuiPickersUtilsProvider';

// Jour d'effet du mouvement : envoyé au serveur en YYYY-MM-DD, jamais en
// timestamp — c'est le serveur qui en dérive le minuit de effective_date,
// dans son propre fuseau (dao/ProductStock.js, getMidnightTimestampForDay).
const DAY_FORMAT = 'YYYY-MM-DD';

// Par défaut on se date au jour de travail de l'app (la date du calendrier),
// pas à aujourd'hui : c'est la date à laquelle l'utilisateur se croit.
function defaultDay(props) {
	return props.selectedDay || moment().format(DAY_FORMAT);
}

function formatPrice(value) {
	if (value === null || value === undefined || value === '') return '';
	const parsed = parseFloat(value);
	return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
}

// Même filtre que la cellule "Prix d'achat HT" de la grille : le signe "-"
// ne passe pas, un prix d'achat négatif n'a pas de sens.
function sanitizePrice(value) {
	return value.replace(/,/g, '.').replace(/[^.\d]/g, '');
}

class ProductStockMovementForm extends Component {
	constructor(props) {
		super(props);
		this.state = {
			step: 'list',
			selectedType: null,
			quantity: '',
			comment: '',
			unit_value: '',
			effective_day: defaultDay(props),
			purchase_price: '',
			follow_type: 'global',
			print_report: true,
			alert_value: '1',
			pendingAutoAdvance: false,
		};
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.open && this.props.open) {
			this.setState({
				step: 'list',
				selectedType: null,
				quantity: '',
				comment: '',
				unit_value: '',
				effective_day: defaultDay(this.props),
				purchase_price: formatPrice(
					this.props.product ? this.props.product.purchase_price : null
				),
				follow_type: 'global',
				print_report: true,
				alert_value: '1',
				pendingAutoAdvance: true,
			});
		}
		if (
			prevProps.movementsLoading &&
			!this.props.movementsLoading &&
			this.state.pendingAutoAdvance
		) {
			if (this.props.movements.length === 0 && this.state.step === 'list') {
				const isUnmanaged = this.props.product && this.props.product.id_stock === null;
				const isService = this.props.product && this.props.product.follow_type === 'service';
				if (!isService) {
					// Produit sans suivi : on demande d'abord ce qu'on vient
					// faire — activer le suivi, ou seulement poser un prix.
					this.setState({ step: isUnmanaged ? 'choice' : 'type', pendingAutoAdvance: false });
				} else {
					this.setState({ pendingAutoAdvance: false });
				}
			} else {
				this.setState({ pendingAutoAdvance: false });
			}
		}
	}

	handleAddNew = () => {
		const isUnmanaged = this.props.product && this.props.product.id_stock === null;
		this.setState({ step: isUnmanaged ? 'choice' : 'type' });
	};

	handleChoiceTracking = () => {
		this.setState({ step: 'config' });
	};

	handleChoicePriceOnly = () => {
		this.setState({ step: 'price' });
	};

	handleSubmitPriceOnly = () => {
		const { purchase_price, effective_day } = this.state;
		const parsed = parseFloat(purchase_price);
		if (!Number.isFinite(parsed) || parsed < 0) return;
		this.props.onSubmitPurchasePrice({
			id_product: this.props.product.id_global,
			unit_value: parsed,
			effective_day: effective_day || defaultDay(this.props),
		});
		this.handleClose();
	};

	handleTypeSelect = type => {
		this.setState({ step: 'form', selectedType: type });
	};

	handleBack = () => {
		const { step } = this.state;
		const isUnmanaged = this.props.product && this.props.product.id_stock === null;
		if (step === 'form') {
			this.setState({
				step: 'type',
				selectedType: null,
				quantity: '',
				comment: '',
				unit_value: '',
				effective_day: defaultDay(this.props),
			});
		} else if (step === 'price') {
			this.setState({ step: 'choice' });
		} else if (step === 'type' && isUnmanaged) {
			this.setState({ step: 'config' });
		} else if (step === 'config') {
			this.setState({ step: 'choice' });
		} else if (step === 'type' || step === 'choice') {
			this.setState({ step: 'list' });
		} else if (step === 'list') {
			this.handleClose();
		}
	};

	handleClose = () => {
		this.props.onClose();
	};

	handleConfigNext = () => {
		this.setState({ step: 'type' });
	};

	// Un input type="number" laisse saisir un signe "-" : inputProps.min n'est
	// qu'une contrainte de validation HTML, pas de saisie. Or ce unit_value est
	// lu comme prix d'achat du produit (getLatestPurchasePrice côté serveur),
	// un négatif fausserait donc le dernier prix connu.
	handleUnitValueChange = e => {
		const value = e.target.value;
		if (value !== '' && parseFloat(value) < 0) return;
		this.setState({ unit_value: value });
	};

	handleEffectiveDayChange = date => {
		this.setState({ effective_day: moment(date).format(DAY_FORMAT) });
	};

	handleSubmit = () => {
		const { selectedType, quantity, comment, unit_value, effective_day, follow_type, print_report, alert_value } = this.state;
		const { product } = this.props;
		const isEntry = selectedType.direction === 1;
		const qty = parseFloat(quantity);
		if (quantity === '' || isNaN(qty) || (isEntry ? qty < 0 : qty <= 0)) return;
		// filet de sécurité si une valeur négative arrivait autrement qu'au
		// clavier (collage, remplissage automatique) — cf. handleUnitValueChange
		if (isEntry && unit_value !== '' && parseFloat(unit_value) < 0) return;
		const delta = isEntry ? parseFloat(quantity) : -parseFloat(quantity);
		const isUnmanaged = product.id_stock === null;

		const data = {
			id_product: product.id_global,
			product_name: product.name,
			id_type_movement: selectedType.id,
			quantity: parseFloat(quantity),
			comment: comment || '',
			unit_value: isEntry && unit_value ? parseFloat(unit_value) : 0,
			total_value: isEntry && unit_value ? parseFloat(unit_value) * parseFloat(quantity) : 0,
			// jour d'effet, pas un timestamp — cf. DAY_FORMAT
			effective_day: effective_day || moment().format(DAY_FORMAT),
			delta,
		};

		if (isUnmanaged) {
			data.quantityInfoData = {
				id_product: product.id_global,
				follow_type,
				print_report,
				alert_value: parseFloat(alert_value) || 1,
			};
		}

		this.props.onSubmit(data);
		this.handleClose();
	};

	formatDate(timestamp) {
		if (!timestamp) return '—';
		const d = new Date(timestamp * 1000);
		return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
	}

	// effective_date est un jour (minuit), pas un instant : on n'affiche donc
	// pas d'heure, contrairement à formatDate qui rend l'horodatage de saisie.
	formatEffectiveDate(timestamp) {
		if (!timestamp) return '—';
		return new Date(timestamp * 1000).toLocaleDateString('fr-FR');
	}

	render() {
		const { open, product, typeMovements, movements, movementsLoading, onDeleteMovement } = this.props;
		const { step, selectedType, quantity, comment, unit_value, effective_day, purchase_price, follow_type, print_report, alert_value } = this.state;

		const isUnmanaged = product && product.id_stock === null;
		const entries = (typeMovements || []).filter(t => t.direction === 1);
		const exits = (typeMovements || []).filter(t => t.direction !== 1);

		const isEntry = selectedType && selectedType.direction === 1;
		const newStock = product
			? Number(product.current_stock) +
			  Number(product.pending_sum) +
			  (selectedType ? (isEntry ? parseFloat(quantity) || 0 : -(parseFloat(quantity) || 0)) : 0)
			: 0;

		const showBack = step !== 'list';

		return (
			<Dialog
				open={open}
				onClose={this.handleClose}
				maxWidth="xs"
				fullWidth
				classes={{ paper: 'stock-movement-form' }}>
				<DialogTitle>
					{showBack && (
						<IconButton onClick={this.handleBack} style={{ marginRight: 8, padding: 4 }}>
							<ArrowBackIcon />
						</IconButton>
					)}
					{product ? product.name : ''}
					{isUnmanaged && (
						<div style={{ fontSize: 12, color: '#e84249', fontWeight: 400, marginTop: 2 }}>
							{I18n.t('productStock.unmanagedLabel')}
						</div>
					)}
				</DialogTitle>

				{step === 'list' && (
					<>
						<DialogContent style={{ padding: 0, minHeight: 80 }}>
							{movementsLoading ? (
								<div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
									<CircularProgress size={28} />
								</div>
							) : movements.length === 0 ? (
								<div style={{ padding: '16px 24px', color: '#999', fontSize: 14 }}>
									{I18n.t('productStock.noMovementPending')}
								</div>
							) : (
								<List dense>
									{movements.map(m => {
										const isE = m.direction === 1;
										const delta = isE ? m.quantity : -m.quantity;
										return (
											<ListItem key={m.id} style={{ paddingRight: 56 }}>
												<ListItemText
													primary={
														<span>
															<span style={{ color: isE ? '#2e7d32' : '#c62828', fontWeight: 600, marginRight: 8 }}>
																{isE ? '+' : '-'}{m.quantity}
															</span>
															{m.label}
														</span>
													}
													secondary={
														<span>
															{this.formatDate(m.date)}
															{' — '}
															{I18n.t('productStock.effectiveOn', {
																date: this.formatEffectiveDate(m.effective_date),
															})}
															{Number(m.unit_value) !== 0 ? ` — ${Number(m.unit_value).toFixed(2)} € HT` : ''}
															{m.comment ? ` — ${m.comment}` : ''}
														</span>
													}
												/>
												<ListItemSecondaryAction>
													<IconButton
														size="small"
														onClick={() => onDeleteMovement(m.id, product.id_global, -delta)}>
														<DeleteIcon fontSize="small" style={{ color: '#e84249' }} />
													</IconButton>
												</ListItemSecondaryAction>
											</ListItem>
										);
									})}
								</List>
							)}
						</DialogContent>
						<DialogActions style={{ justifyContent: 'space-between' }}>
							<Button onClick={this.handleClose}>{I18n.t('common.cancel')}</Button>
							{product && product.follow_type === 'service' ? (
								<span style={{ fontSize: 12, color: '#999', fontStyle: 'italic', alignSelf: 'center' }}>
									{I18n.t('productStock.serviceMovementBlocked')}
								</span>
							) : (
								<Button onClick={this.handleAddNew} color="primary" variant="raised">
									<AddIcon style={{ marginRight: 4, fontSize: 18 }} />
									{I18n.t('productStock.addMovement')}
								</Button>
							)}
						</DialogActions>
					</>
				)}

				{step === 'choice' && (
					<>
						<DialogContent>
							{/* Deux façons d'utiliser un produit sans suivi : lui ouvrir
							    une gestion de stock, ou juste lui attacher un prix
							    d'achat — le second ne crée aucun product_quantity_info,
							    le produit reste dans "Aucun suivi". */}
							<Button
								variant="raised"
								color="primary"
								fullWidth
								style={{ textTransform: 'none', padding: '12px 16px', marginBottom: 12 }}
								onClick={this.handleChoiceTracking}>
								{I18n.t('productStock.choiceTracking')}
							</Button>
							<Button
								variant="outlined"
								color="primary"
								fullWidth
								style={{ textTransform: 'none', padding: '12px 16px' }}
								onClick={this.handleChoicePriceOnly}>
								{I18n.t('productStock.choicePriceOnly')}
							</Button>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleClose}>{I18n.t('common.cancel')}</Button>
						</DialogActions>
					</>
				)}

				{step === 'price' && (
					<>
						<DialogContent>
							<div style={{ display: 'flex', marginBottom: 16 }}>
								<div style={{ flex: 1, marginRight: 16 }}>
									<TextField
										label={I18n.t('productStock.unitPrice')}
										value={purchase_price}
										onChange={e => this.setState({ purchase_price: sanitizePrice(e.target.value) })}
										fullWidth
										autoFocus
									/>
								</div>
								<div style={{ flex: 1 }}>
									<MuiPickersUtilsProvider
										utils={MomentUtils}
										moment={moment}
										locale={moment.locale(window.navigator.language)}>
										<DatePicker
											label={I18n.t('productStock.effectiveDate')}
											cancelLabel={I18n.t('generalButtons.cancel')}
											format={'DD MMMM YYYY'}
											value={effective_day}
											onChange={this.handleEffectiveDayChange}
											fullWidth
										/>
									</MuiPickersUtilsProvider>
								</div>
							</div>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleClose}>{I18n.t('common.cancel')}</Button>
							<Button
								onClick={this.handleSubmitPriceOnly}
								color="primary"
								variant="raised"
								disabled={!Number.isFinite(parseFloat(purchase_price))}>
								{I18n.t('common.save')}
							</Button>
						</DialogActions>
					</>
				)}

				{step === 'config' && (
					<>
						<DialogContent>
							<FormLabel style={{ display: 'block', marginBottom: 12 }}>
								{I18n.t('productStock.followTypeLabel')}
							</FormLabel>
							<RadioGroup
								value={follow_type}
								onChange={e => this.setState({ follow_type: e.target.value })}
								style={{ marginBottom: 16 }}>
								<FormControlLabel value="global" control={<Radio />} label={I18n.t('productStock.followTypeGlobal')} />
							</RadioGroup>

							<TextField
								label={I18n.t('productStock.alertValueLabel')}
								type="number"
								value={alert_value}
								onChange={e => this.setState({ alert_value: e.target.value })}
								inputProps={{ min: 0, step: 1 }}
								fullWidth
								style={{ marginBottom: 4 }}
							/>
							<div style={{ fontSize: 11, color: '#999', marginBottom: 16 }}>
								{I18n.t('productStock.alertValueHint')}
							</div>

							<FormControlLabel
								control={
									<Switch
										checked={print_report}
										onChange={e => this.setState({ print_report: e.target.checked })}
										color="primary"
									/>
								}
								label={I18n.t('productStock.printReportLabel')}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleClose}>{I18n.t('common.cancel')}</Button>
							<Button onClick={this.handleConfigNext} color="primary" variant="raised">
								{I18n.t('productStock.next')}
							</Button>
						</DialogActions>
					</>
				)}

				{step === 'type' && (
					<DialogContent style={{ padding: 0 }}>
						<List subheader={<ListSubheader>{I18n.t('productStock.entry')}</ListSubheader>}>
							{entries.map(type => (
								<ListItem button key={type.id} onClick={() => this.handleTypeSelect(type)}>
									<ListItemText primary={type.label} />
								</ListItem>
							))}
						</List>
						<List subheader={<ListSubheader>{I18n.t('productStock.exit')}</ListSubheader>}>
							{exits.map(type => (
								<ListItem
									button
									key={type.id}
									onClick={() => this.handleTypeSelect(type)}
									disabled={product && Number(product.current_stock) + Number(product.pending_sum) <= 0}>
									<ListItemText primary={type.label} />
								</ListItem>
							))}
						</List>
					</DialogContent>
				)}

				{step === 'form' && selectedType && (
					<>
						<DialogContent>
							<div style={{ marginBottom: 16, color: '#666', fontSize: 14 }}>
								<strong>{selectedType.label}</strong>
								{' — '}
								{isEntry ? I18n.t('productStock.entry') : I18n.t('productStock.exit')}
							</div>

							<TextField
								label={I18n.t('productStock.quantity')}
								type="number"
								value={quantity}
								onChange={e => this.setState({ quantity: e.target.value })}
								inputProps={{ min: 1, step: 1 }}
								fullWidth
								autoFocus
								style={{ marginBottom: 16 }}
							/>

							<div style={{ marginBottom: 16, fontSize: 13, color: !isEntry && newStock < 0 ? '#e84249' : '#666' }}>
								{I18n.t('productStock.stockAfterMovement')}
								{' : '}
								<strong>{newStock}</strong>
								{!isEntry && newStock < 0 && (
									<span style={{ marginLeft: 8 }}>— {I18n.t('productStock.insufficientStock')}</span>
								)}
							</div>

							{/* Prix d'achat et date d'effet sur la même ligne : la date
							    qualifie le mouvement entier (elle reste donc affichée pour
							    une sortie, seule sur sa ligne), mais c'est avec le prix
							    qu'elle se lit — à partir de quand ce prix s'applique.
							    Aucune borne : on peut planifier une livraison à venir comme
							    régulariser un mouvement oublié. */}
							<div style={{ display: 'flex', marginBottom: 16 }}>
								{isEntry && (
									<div style={{ flex: 1, marginRight: 16 }}>
										<TextField
											label={I18n.t('productStock.unitPrice')}
											type="number"
											value={unit_value}
											onChange={this.handleUnitValueChange}
											inputProps={{ min: 0, step: 0.01 }}
											fullWidth
										/>
									</div>
								)}
								<div style={{ flex: 1 }}>
									<MuiPickersUtilsProvider
										utils={MomentUtils}
										moment={moment}
										locale={moment.locale(window.navigator.language)}>
										<DatePicker
											label={I18n.t('productStock.effectiveDate')}
											cancelLabel={I18n.t('generalButtons.cancel')}
											format={'DD MMMM YYYY'}
											value={effective_day}
											onChange={this.handleEffectiveDayChange}
											fullWidth
										/>
									</MuiPickersUtilsProvider>
								</div>
							</div>

							<TextField
								label={I18n.t('productStock.comment')}
								value={comment}
								onChange={e => this.setState({ comment: e.target.value })}
								fullWidth
								multiline
								rows={2}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleClose}>{I18n.t('common.cancel')}</Button>
							<Button
								onClick={this.handleSubmit}
								color="primary"
								variant="raised"
								disabled={quantity === '' || isNaN(parseFloat(quantity)) || (isEntry ? parseFloat(quantity) < 0 : parseFloat(quantity) <= 0) || (!isEntry && newStock < 0)}>
								{I18n.t('common.save')}
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		);
	}
}

export default ProductStockMovementForm;
